/**
 * Das Benutzer-Objektblatt (Einrichten-Neubau P4).
 *
 * Entmischt, was die alte Detailseite vermengte: Stammdaten haben EIN
 * Speichern; alles, was sofort wirkt, ist eine bewusste, zweistufige
 * Aktion in eigener Zone (Chip anlernen, Einladung, Passwort, 2FA) —
 * kein natives confirm mehr, kein Formular, das je nach Rolle Sektionen
 * ein- und ausblendet: E-Mail ist immer sichtbar, bei Mitarbeitern
 * schlicht als „nur für die Anmeldung am Leitstand nötig" erklärt.
 * Der „Neue Mitarbeiter"-Weg ist dieses Blatt im Anlege-Modus: Name,
 * Rolle, anlegen — danach steht der Chip-Schritt direkt bereit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Notiz, Zustand } from '../../components/leitstand';
import { ROLLEN_LABELS } from './BenutzerListe';

const EINGABE =
  'w-full rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

interface Benutzer {
  id: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  externalEmployeeId?: string | null;
  role: string;
  isActive: boolean;
  hatChip?: boolean;
  chipKurz?: string | null;
  hatChipnummer?: boolean;
  twoFactorEnabled?: boolean;
  inviteSentAt?: string | null;
  inviteUsedAt?: string | null;
}

/** Welche Aktion gerade ihre zweistufige Bestätigung zeigt */
type OffeneAktion = 'chip' | 'chipWeg' | 'einladung' | 'passwort' | 'zweiFaktor' | 'zustand' | null;

export default function BenutzerBlatt() {
  const { user } = useAuth();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const istNeu = id === 'neu';

  const [b, setB] = useState<Benutzer | null>(null);
  const [form, setForm] = useState({
    displayName: '',
    firstName: '',
    lastName: '',
    email: '',
    externalEmployeeId: '',
    role: 'WORKER',
  });
  const [laden, setLaden] = useState(!istNeu);
  const [fehler, setFehler] = useState('');
  const [aktionsFehler, setAktionsFehler] = useState('');
  const [gespeichert, setGespeichert] = useState('');
  const [speichert, setSpeichert] = useState(false);

  const [offen, setOffen] = useState<OffeneAktion>(null);
  const [chipWert, setChipWert] = useState('');
  const [passwortWert, setPasswortWert] = useState('');
  const chipFeld = useRef<HTMLInputElement>(null);

  const [verwendung, setVerwendung] = useState<
    Array<{ text: string; wert: number; ziel?: string }>
  >([]);
  const [verlauf, setVerlauf] = useState<Array<{ wann: string; wer: string; was: string }>>([]);

  const lade = useCallback(async () => {
    if (istNeu) return;
    setLaden(true);
    setFehler('');
    try {
      const [oRes, vRes, hRes] = await Promise.all([
        fetch(`/api/einrichten/objekt/benutzer/${id}`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/benutzer/${id}/verwendung`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/benutzer/${id}/verlauf`, { credentials: 'include' }),
      ]);
      if (!oRes.ok) throw new Error('Der Benutzer konnte nicht geladen werden');
      const o = (await oRes.json()).objekt as Benutzer;
      setB(o);
      setForm({
        displayName: o.displayName ?? '',
        firstName: o.firstName ?? '',
        lastName: o.lastName ?? '',
        email: o.email ?? '',
        externalEmployeeId: o.externalEmployeeId ?? '',
        role: o.role,
      });
      if (vRes.ok) setVerwendung((await vRes.json()).zeilen ?? []);
      if (hRes.ok) setVerlauf((await hRes.json()).zeilen ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Benutzer konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [id, istNeu]);

  useEffect(() => {
    void lade();
  }, [lade]);

  // Chip-Anlernen: das Feld fängt den Scanner (tippt wie eine Tastatur)
  useEffect(() => {
    if (offen === 'chip') chipFeld.current?.focus();
  }, [offen]);

  const api = async (pfad: string, methode: string, body?: unknown) => {
    setAktionsFehler('');
    const res = await fetch(pfad, {
      method: methode,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const antwort = await res.json().catch(() => null);
    if (!res.ok) throw new Error(antwort?.error || 'Aktion fehlgeschlagen');
    return antwort;
  };

  const melde = (text: string) => {
    setGespeichert(text);
    setTimeout(() => setGespeichert(''), 2600);
  };

  const speichere = async () => {
    if (!form.displayName.trim()) {
      setAktionsFehler('Bitte einen Anzeigenamen angeben.');
      return;
    }
    setSpeichert(true);
    try {
      const body = {
        displayName: form.displayName.trim(),
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        email: form.email.trim() || null,
        role: form.role,
      };
      if (istNeu) {
        const antwort = await api('/api/admin/users', 'POST', body);
        const neueId = antwort?.id ?? antwort?.user?.id;
        navigate(neueId ? `/einrichten/benutzer/${neueId}` : '/einrichten/benutzer', {
          replace: true,
        });
        return;
      }
      await api(`/api/admin/users/${id}`, 'PATCH', body);
      melde('✓ Gespeichert');
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const aktion = async (tue: () => Promise<void>, erfolg: string) => {
    try {
      await tue();
      setOffen(null);
      melde(erfolg);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Aktion fehlgeschlagen');
    }
  };

  const zone = (titel: string, kinder: React.ReactNode) => (
    <div className="flex flex-col gap-2 border-t border-linie pt-4 first:border-t-0 first:pt-0">
      <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
        {titel}
      </span>
      {kinder}
    </div>
  );

  const rolleName = ROLLEN_LABELS[form.role] ?? form.role;

  return (
    <LeitstandRahmen
      titel={istNeu ? 'Mitarbeiter anlegen' : (b?.displayName ?? 'Benutzer')}
      benutzer={user?.displayName}
      werkzeuge={
        !istNeu && b ? (
          <span className="flex items-center gap-3">
            {b.isActive ? (
              <Zustand art="gut">aktiv</Zustand>
            ) : (
              <span className="font-zahl text-[11px] text-tinte-3">außer Betrieb</span>
            )}
            <button
              onClick={() =>
                b.isActive
                  ? setOffen('zustand')
                  : void aktion(async () => {
                      await api(`/api/einrichten/objekt/benutzer/${id}/zustand`, 'POST', {
                        aktiv: true,
                      });
                    }, '✓ Wieder in Betrieb')
              }
              className={KNOPF_LEISE}
            >
              {b.isActive ? 'Außer Betrieb nehmen' : 'Wieder in Betrieb nehmen'}
            </button>
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6">
        <Link to="/einrichten/benutzer" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Benutzer
        </Link>

        {offen === 'zustand' && b && (
          <div className="flex flex-wrap items-center gap-3 rounded-leitstand border border-linie-2 bg-flaeche px-4 py-3">
            <span className="text-[13.5px] text-tinte-2">
              {b.displayName} kann sich dann nicht mehr anmelden und erscheint nicht mehr an den
              Stationen. Alles Erfasste bleibt unter diesem Namen erhalten; Wiederinbetriebnahme
              jederzeit.
            </span>
            <button
              onClick={() =>
                void aktion(async () => {
                  await api(`/api/einrichten/objekt/benutzer/${id}/zustand`, 'POST', {
                    aktiv: false,
                  });
                }, '✓ Außer Betrieb')
              }
              className={KNOPF_PRIMAER}
            >
              Außer Betrieb nehmen
            </button>
            <button onClick={() => setOffen(null)} className={KNOPF_LEISE}>
              Abbrechen
            </button>
          </div>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Benutzer …</p>
        )}

        {!laden && !fehler && (
          <>
            <Karte titel="Stammdaten" marke="ein Speichern, eine Rückmeldung">
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Anzeigename *
                    </span>
                    <input
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                      className={EINGABE}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Vorname
                    </span>
                    <input
                      value={form.firstName}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                      className={EINGABE}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Nachname
                    </span>
                    <input
                      value={form.lastName}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                      className={EINGABE}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Rolle
                    </span>
                    <select
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className={EINGABE}
                    >
                      {Object.entries(ROLLEN_LABELS)
                        .filter(([code]) => code !== 'SYSTEM' || form.role === 'SYSTEM')
                        .map(([code, label]) => (
                          <option key={code} value={code}>
                            {label}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      E-Mail
                    </span>
                    <input
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={EINGABE}
                    />
                    {form.role === 'WORKER' && (
                      <span className="text-xs text-tinte-3">
                        Nur nötig, wenn {form.displayName.trim() || 'die Person'} sich am Leitstand
                        anmelden soll.
                      </span>
                    )}
                  </label>
                  {!istNeu && form.externalEmployeeId && (
                    <label className="flex flex-col gap-1.5">
                      <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                        Personalnummer
                      </span>
                      <input value={form.externalEmployeeId} disabled className={EINGABE} />
                      <span className="text-xs text-tinte-3">aus dem HR-Import — dort führend</span>
                    </label>
                  )}
                </div>
                {aktionsFehler && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => void speichere()}
                    disabled={speichert}
                    className={KNOPF_PRIMAER}
                  >
                    {istNeu ? 'Anlegen' : 'Speichern'}
                  </button>
                  {gespeichert && (
                    <span className="font-zahl text-[13px] text-lauf">{gespeichert}</span>
                  )}
                  {istNeu && (
                    <span className="text-xs text-tinte-3">
                      Nach dem Anlegen steht hier der Chip-Schritt bereit.
                    </span>
                  )}
                </div>
              </div>
            </Karte>

            {!istNeu && b && (
              <Karte titel="Chip & Zugang" marke="bewusste Aktionen — jede fragt einmal nach">
                <div className="flex flex-col gap-4">
                  {zone(
                    'Chip an der Station',
                    <div className="flex flex-wrap items-center gap-3">
                      {b.hatChip ? (
                        <span className="text-sm">
                          Chip angelernt{' '}
                          <span className="font-zahl text-tinte-3">(…{b.chipKurz})</span>
                        </span>
                      ) : (
                        <span className="text-sm text-tinte-2">
                          Kein Chip angelernt — ohne Chip keine Anmeldung an den Stationen.
                        </span>
                      )}
                      {offen !== 'chip' && offen !== 'chipWeg' && (
                        <>
                          <button
                            onClick={() => {
                              setChipWert('');
                              setOffen('chip');
                            }}
                            className={KNOPF_LEISE}
                          >
                            {b.hatChip ? 'Chip neu anlernen' : 'Chip anlernen'}
                          </button>
                          {b.hatChip && (
                            <button onClick={() => setOffen('chipWeg')} className={KNOPF_LEISE}>
                              Chip entfernen
                            </button>
                          )}
                        </>
                      )}
                      {offen === 'chip' && (
                        <span className="flex flex-wrap items-center gap-2">
                          <input
                            ref={chipFeld}
                            value={chipWert}
                            onChange={(e) => setChipWert(e.target.value)}
                            placeholder="Chip ans Lesegerät halten …"
                            className={`${EINGABE} w-56`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && chipWert.trim())
                                void aktion(async () => {
                                  await api(`/api/admin/users/${id}`, 'PATCH', {
                                    nfcChipId: chipWert.trim(),
                                  });
                                }, '✓ Chip angelernt');
                            }}
                          />
                          <button
                            onClick={() =>
                              void aktion(async () => {
                                await api(`/api/admin/users/${id}`, 'PATCH', {
                                  nfcChipId: chipWert.trim(),
                                });
                              }, '✓ Chip angelernt')
                            }
                            disabled={!chipWert.trim()}
                            className={KNOPF_PRIMAER}
                          >
                            Übernehmen
                          </button>
                          <button onClick={() => setOffen(null)} className={KNOPF_LEISE}>
                            Abbrechen
                          </button>
                        </span>
                      )}
                      {offen === 'chipWeg' && (
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-[13px] text-tinte-2">
                            {b.displayName} kann sich dann an keiner Station mehr anmelden.
                          </span>
                          <button
                            onClick={() =>
                              void aktion(async () => {
                                await api(`/api/admin/users/${id}`, 'PATCH', { nfcChipId: null });
                              }, '✓ Chip entfernt')
                            }
                            className={KNOPF_PRIMAER}
                          >
                            Ja, entfernen
                          </button>
                          <button onClick={() => setOffen(null)} className={KNOPF_LEISE}>
                            Abbrechen
                          </button>
                        </span>
                      )}
                    </div>,
                  )}

                  {zone(
                    'Anmeldung am Leitstand',
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-tinte-2">
                        {b.inviteUsedAt
                          ? 'Einladung angenommen — Zugang eingerichtet.'
                          : b.inviteSentAt
                            ? `Einladung verschickt am ${new Date(b.inviteSentAt).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' })} — noch nicht angenommen.`
                            : 'Noch kein Leitstand-Zugang eingerichtet.'}
                        {b.twoFactorEnabled ? ' Zwei-Faktor ist aktiv.' : ''}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        {offen !== 'einladung' &&
                          offen !== 'passwort' &&
                          offen !== 'zweiFaktor' && (
                            <>
                              <button
                                onClick={() => setOffen('einladung')}
                                disabled={!form.email.trim()}
                                className={KNOPF_LEISE}
                              >
                                {b.inviteSentAt ? 'Einladung erneut senden' : 'Einladung senden'}
                              </button>
                              <button
                                onClick={() => {
                                  setPasswortWert('');
                                  setOffen('passwort');
                                }}
                                className={KNOPF_LEISE}
                              >
                                Passwort setzen
                              </button>
                              {b.twoFactorEnabled && (
                                <button
                                  onClick={() => setOffen('zweiFaktor')}
                                  className={KNOPF_LEISE}
                                >
                                  Zwei-Faktor zurücksetzen
                                </button>
                              )}
                            </>
                          )}
                        {!form.email.trim() && offen === null && (
                          <span className="text-xs text-tinte-3">
                            Für die Einladung zuerst eine E-Mail speichern.
                          </span>
                        )}
                        {offen === 'einladung' && (
                          <>
                            <span className="text-[13px] text-tinte-2">
                              Einladung an {form.email} senden?
                            </span>
                            <button
                              onClick={() =>
                                void aktion(async () => {
                                  await api('/api/auth/invite', 'POST', { userId: id });
                                }, '✓ Einladung gesendet')
                              }
                              className={KNOPF_PRIMAER}
                            >
                              Ja, senden
                            </button>
                            <button onClick={() => setOffen(null)} className={KNOPF_LEISE}>
                              Abbrechen
                            </button>
                          </>
                        )}
                        {offen === 'passwort' && (
                          <>
                            <input
                              type="password"
                              value={passwortWert}
                              onChange={(e) => setPasswortWert(e.target.value)}
                              placeholder="Neues Passwort …"
                              className={`${EINGABE} w-56`}
                              autoFocus
                            />
                            <button
                              onClick={() =>
                                void aktion(async () => {
                                  await api(`/api/admin/users/${id}`, 'PATCH', {
                                    password: passwortWert,
                                  });
                                }, '✓ Passwort gesetzt')
                              }
                              disabled={passwortWert.length < 8}
                              className={KNOPF_PRIMAER}
                            >
                              Setzen
                            </button>
                            <button onClick={() => setOffen(null)} className={KNOPF_LEISE}>
                              Abbrechen
                            </button>
                            {passwortWert.length > 0 && passwortWert.length < 8 && (
                              <span className="text-xs text-stand">mindestens 8 Zeichen</span>
                            )}
                          </>
                        )}
                        {offen === 'zweiFaktor' && (
                          <>
                            <span className="text-[13px] text-tinte-2">
                              {b.displayName} muss Zwei-Faktor danach neu einrichten.
                            </span>
                            <button
                              onClick={() =>
                                void aktion(async () => {
                                  await api(`/api/admin/users/${id}/reset-2fa`, 'POST');
                                }, '✓ Zwei-Faktor zurückgesetzt')
                              }
                              className={KNOPF_PRIMAER}
                            >
                              Ja, zurücksetzen
                            </button>
                            <button onClick={() => setOffen(null)} className={KNOPF_LEISE}>
                              Abbrechen
                            </button>
                          </>
                        )}
                      </div>
                      {form.role === 'WORKER' && (
                        <span className="text-xs text-tinte-3">
                          {rolleName} arbeiten normalerweise nur mit dem Chip — Leitstand-Zugang ist
                          die Ausnahme.
                        </span>
                      )}
                    </div>,
                  )}
                </div>
              </Karte>
            )}

            {!istNeu && (
              <Karte titel="Verwendung" marke="Wirkung sichtbar, bevor man handelt">
                {verwendung.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">Noch nichts erfasst.</p>
                ) : (
                  <div className="flex flex-col">
                    {verwendung.map((v, i) => {
                      const inhalt = (
                        <>
                          <span className="font-zahl text-[15px] font-medium">
                            {v.wert.toLocaleString('de-DE')}
                          </span>
                          <span className="text-sm">{v.text}</span>
                          {v.ziel && <span className="ml-auto font-zahl text-tinte-3">→</span>}
                        </>
                      );
                      const klasse = `flex items-center gap-3 px-1 py-3 ${i < verwendung.length - 1 ? 'border-b border-linie' : ''}`;
                      return v.ziel ? (
                        <Link key={v.text} to={v.ziel} className={`${klasse} hover:bg-flaeche-2`}>
                          {inhalt}
                        </Link>
                      ) : (
                        <div key={v.text} className={klasse}>
                          {inhalt}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Karte>
            )}

            {!istNeu && (
              <Karte
                titel="Verlauf"
                marke="Änderungen am Benutzer — Anmeldungen stehen im Protokoll"
              >
                {verlauf.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">
                    Noch keine Einträge im Protokoll.
                  </p>
                ) : (
                  <div className="flex flex-col">
                    {verlauf.map((z, i) => (
                      <div
                        key={`${z.wann}-${i}`}
                        className={`grid grid-cols-[110px_130px_1fr] gap-3 px-1 py-2.5 text-[13px] max-sm:grid-cols-[90px_1fr] ${
                          i < verlauf.length - 1 ? 'border-b border-linie' : ''
                        }`}
                      >
                        <span className="font-zahl text-xs text-tinte-3">
                          {new Date(z.wann).toLocaleString('de-DE', {
                            timeZone: 'Europe/Berlin',
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span className="text-tinte-2">{z.wer}</span>
                        <span className="max-sm:col-span-2">{z.was}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Karte>
            )}
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
